import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { XMarkIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { Campaign } from '../../types';
import { graphqlApi } from '../../services/graphql';
import { Spinner } from '../common/Spinner';

interface CampaignEditFormProps {
  campaign: Campaign;
  onComplete: (updatedCampaign: Campaign) => void | Promise<void>;
  onCancel: () => void;
}

export const CampaignEditForm: React.FC<CampaignEditFormProps> = ({
  campaign,
  onComplete,
  onCancel
}) => {
  const { t } = useTranslation('campaigns');
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>(campaign.representativeImages?.[0]?.fileUrl || campaign.imageUrl || '');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    title: campaign.campaignName || campaign.title || '',
    description: campaign.productIntroduction || campaign.description || '',
    category: campaign.campaignCategory || campaign.category || '',
    budget: campaign.budget || 0,
    budgetRange: campaign.budgetRange || '',
    location: campaign.location || '',
    minFollowers: campaign.requirements?.minFollowers || 0,
    minEngagementRate: campaign.requirements?.minEngagementRate || 0,
    platforms: campaign.requirements?.platforms || [],
    targetAudience: campaign.requirements?.targetAudience || [],
    contentType: campaign.deliverables?.contentType || [],
    contentCount: campaign.deliverables?.contentCount || 1,
    contentGuidelines: campaign.deliverables?.contentGuidelines || '',
    startDate: campaign.deliverables?.timeline?.startDate || '',
    endDate: campaign.deliverables?.timeline?.endDate || '',
    deliveryDeadline: campaign.deliverables?.timeline?.deliveryDeadline || ''
  });

  const uploadImageToS3 = async (file: File): Promise<string> => {
    try {
      setUploadingImage(true);
      
      // Get pre-signed URL from backend
      const uploadUrlResponse = await graphqlApi.getUploadUrl(
        file.name,
        file.type,
        file.size
      );

      if (!uploadUrlResponse.success || !uploadUrlResponse.data) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl, fileUrl } = uploadUrlResponse.data;

      // Upload file to S3 using pre-signed URL
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image to S3');
      }

      console.log('✅ Image uploaded successfully:', fileUrl);
      return fileUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error(t('messages.invalidImageType'));
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('messages.imageTooLarge'));
      return;
    }

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageRemove = () => {
    setImageFile(null);
    setImagePreview(campaign.representativeImages?.[0]?.fileUrl || campaign.imageUrl || '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload new image if selected
      let imageUrl = campaign.representativeImages?.[0]?.fileUrl || campaign.imageUrl;
      if (imageFile) {
        try {
          const uploadedUrl = await uploadImageToS3(imageFile);
          console.log('🖼️ New image uploaded:', uploadedUrl);
          console.log('📍 Old image URL:', campaign.imageUrl);
          imageUrl = uploadedUrl;
        } catch (error) {
          toast.error(t('messages.imageUploadFailed'));
          setLoading(false);
          return;
        }
      }

      console.log('💾 Saving campaign with imageUrl:', imageUrl);

      const updateData: any = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        status: campaign.status,
        ...(imageUrl && { imageUrl }),
        ...(formData.budget && { budget: formData.budget }),
        ...(formData.budgetRange && { budgetRange: formData.budgetRange }),
        ...(formData.location && { location: formData.location })
      };

      // Add requirements
      updateData.requirements = {
        minFollowers: formData.minFollowers || 0,
        targetAudience: formData.targetAudience,
        languages: campaign.requirements?.languages || [],
        location: campaign.requirements?.location || [],
        platforms: formData.platforms,
        ...(campaign.requirements?.maxFollowers && { maxFollowers: campaign.requirements.maxFollowers }),
        ...(formData.minEngagementRate && { minEngagementRate: formData.minEngagementRate }),
        ...(campaign.requirements?.preferredLocations && { preferredLocations: campaign.requirements.preferredLocations }),
        ...(formData.contentType.length > 0 && { contentType: formData.contentType }),
        ...(campaign.requirements?.deliverables && { deliverables: campaign.requirements.deliverables })
      };

      // Add deliverables
      updateData.deliverables = {
        contentType: formData.contentType,
        contentCount: formData.contentCount || 1,
        contentGuidelines: formData.contentGuidelines,
        hashtags: campaign.deliverables?.hashtags || [],
        mentions: campaign.deliverables?.mentions || [],
        ...(formData.startDate && formData.endDate && formData.deliveryDeadline && {
          timeline: {
            startDate: formData.startDate,
            endDate: formData.endDate,
            deliveryDeadline: formData.deliveryDeadline
          }
        })
      };

      const response = await graphqlApi.updateCampaign(campaign.campaignId, updateData);

      console.log('✅ Campaign update response:', response);
      console.log('📸 Updated imageUrl:', response.data?.imageUrl);

      if (response.success && response.data) {
        onComplete(response.data);
      } else {
        toast.error(response.message || t('messages.updateFailed'));
      }
    } catch (error: any) {
      console.error('Error updating campaign:', error);
      toast.error(error.message || t('messages.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{t('edit.title')}</h2>
        <p className="text-gray-600 mt-1">{t('edit.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('edit.basicInfo')}</h3>
          
          <div className="space-y-4">
            {/* Campaign Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('createForm.step1.campaignImage')}
              </label>
              
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Campaign preview"
                    className="w-full h-64 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={handleImageRemove}
                    className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                  {imageFile && (
                    <div className="mt-2 text-sm text-gray-600">
                      New image selected: {imageFile.name}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-gray-400 transition-colors">
                  <div className="space-y-2 text-center">
                    <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="image-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                      >
                        <span>{t('createForm.step1.clickToUpload')}</span>
                        <input
                          id="image-upload"
                          name="image-upload"
                          type="file"
                          className="sr-only"
                          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                          onChange={handleImageSelect}
                        />
                      </label>
                      <p className="pl-1">{t('createForm.step1.orDragAndDrop')}</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      {t('createForm.step1.imageRequirements')}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('form.title')} *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('form.description')} *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.category')} *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select category</option>
                  <option value="Fashion & Beauty">Fashion & Beauty</option>
                  <option value="Travel & Hospitality">Travel & Hospitality</option>
                  <option value="Food & Beverage">Food & Beverage</option>
                  <option value="Technology">Technology</option>
                  <option value="Health & Fitness">Health & Fitness</option>
                  <option value="Lifestyle">Lifestyle</option>
                  <option value="Gaming">Gaming</option>
                  <option value="Education">Education</option>
                  <option value="Finance">Finance</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.location')}
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., New York, USA"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.budget')}
                </label>
                <input
                  type="number"
                  value={formData.budget}
                  onChange={(e) => handleChange('budget', parseFloat(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.budgetRange')}
                </label>
                <input
                  type="text"
                  value={formData.budgetRange}
                  onChange={(e) => handleChange('budgetRange', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., $1,000 - $5,000"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Requirements */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('edit.requirements')}</h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.minFollowers')}
                </label>
                <input
                  type="number"
                  value={formData.minFollowers}
                  onChange={(e) => handleChange('minFollowers', parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.minEngagement')}
                </label>
                <input
                  type="number"
                  value={formData.minEngagementRate}
                  onChange={(e) => handleChange('minEngagementRate', parseFloat(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Deliverables */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('edit.deliverables')}</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('form.contentCount')}
              </label>
              <input
                type="number"
                value={formData.contentCount}
                onChange={(e) => handleChange('contentCount', parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('form.contentGuidelines')}
              </label>
              <textarea
                value={formData.contentGuidelines}
                onChange={(e) => handleChange('contentGuidelines', e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('form.contentGuidelinesPlaceholder')}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.startDate')}
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.endDate')}
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.deliveryDeadline')}
                </label>
                <input
                  type="date"
                  value={formData.deliveryDeadline}
                  onChange={(e) => handleChange('deliveryDeadline', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            {t('form.cancel')}
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            disabled={loading || uploadingImage}
          >
            {(loading || uploadingImage) && <Spinner />}
            <span>
              {uploadingImage 
                ? t('messages.uploadingImage') 
                : loading 
                  ? t('form.saving') 
                  : t('form.saveChanges')
              }
            </span>
          </button>
        </div>
      </form>
    </div>
  );
};
